import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faChartLine, 
    faDatabase, 
    faEnvelope, 
    faDollarSign,
    faReply,
    faArrowTrendUp,
    faCheck,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

interface SponsorApplication {
    _id: string;
    sponsorName: string;
    sponsorId: string;
    contactEmail: string;
    dateApplied: string;
    status: 'pending' | 'responded' | 'follow_up_needed' | 'closed_won' | 'closed_lost';
    responseDate?: string;
    followUpDate?: string;
    revenue?: number;
    notes?: string;
    lastContactDate?: string;
}

interface AnalyticsProps {
    isSubscribed: boolean;
    user?: {
        email: string;
        name?: string;
        isAdmin?: boolean;
        newsletterInfo?: {
            name?: string;
            topic?: string;
            audience_size?: number;
            engagement_rate?: number;
            publishing_frequency?: string;
            audience_demographics?: {
                age_range?: string;
                income_range?: string;
                location?: string;
                interests?: string[];
                job_titles?: string[];
            };
        } | null;
    };
}

const Analytics: React.FC<AnalyticsProps> = ({ isSubscribed, user }) => {
    const navigate = useNavigate();
    const [applications, setApplications] = useState<SponsorApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showRevenueModal, setShowRevenueModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<SponsorApplication | null>(null);
    const [revenueAmount, setRevenueAmount] = useState('');
    const [pipelineSponsors, setPipelineSponsors] = useState<any[]>([]);
    // Default to what actually needs a decision from the user — won/lost
    // deals used to sit in the same un-paginated list as everything else,
    // so an active pitcher had to scroll past their whole closed history
    // just to see what's still open (worse on mobile, where each row
    // stacks into a multi-line card).
    const [applicationsView, setApplicationsView] = useState<'active' | 'closed' | 'all'>('active');

    const fetchAnalyticsData = useCallback(async () => {
        try {
            setLoading(true);
            
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const applicationsResponse = await axios.get(`${config.backendUrl}userApplications`, {
                headers: { 'x-auth-token': token }
            });
            
            setApplications(applicationsResponse.data);

            // Outreach pipeline: sponsors with a tracked status
            try {
                const sponsorsResponse = await axios.get(`${config.backendUrl}sponsors`, {
                    headers: { 'x-auth-token': token }
                });
                setPipelineSponsors(
                    (sponsorsResponse.data || []).filter((sp: any) => sp.outreachStatus || sp.isApplied)
                );
            } catch (pipelineErr) {
                console.error('Error fetching pipeline sponsors:', pipelineErr);
            }

            setLoading(false);
        } catch (err) {
            setError('Failed to load analytics data');
            console.error('Error fetching analytics:', err);
            setLoading(false);
        }
    }, [navigate]);

    const updateOutreachStatus = async (sponsorId: string, type: string) => {
        const token = localStorage.getItem('token');
        if (!token) return;
        setPipelineSponsors(prev => prev.map(sp =>
            sp._id === sponsorId ? { ...sp, outreachStatus: type === 'none' ? null : type, outreachDate: new Date().toISOString() } : sp
        ));
        try {
            await axios.post(`${config.backendUrl}sponsors/${sponsorId}/interaction`, { type }, {
                headers: { 'x-auth-token': token }
            });
        } catch (err) {
            console.error('Failed to update outreach status', err);
        }
    };

    useEffect(() => {
        if (isSubscribed || user?.isAdmin) {
            fetchAnalyticsData();
        }
    }, [fetchAnalyticsData, isSubscribed, user?.isAdmin]);

    // Show subscription prompt for unsubscribed users (unless admin)
    if (!isSubscribed && !user?.isAdmin) {
        return (
            <div className="page-shell page-shell--wide analytics-page">
                <div className="analytics-subscription-prompt">
                    <div className="subscription-prompt-content">
                        <FontAwesomeIcon icon={faChartLine} className="subscription-prompt-icon" />
                        <h2>Access Your Sponsor Analytics</h2>
                        <p>Subscribe to track your outreach performance, revenue, and optimize your sponsor strategy.</p>
                        <button 
                            className="btn btn-primary"
                            onClick={() => navigate('/subscribe')}
                        >
                            Subscribe Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Helper functions for analytics calculations
    const getTotalRevenue = () => {
        return applications.reduce((sum, app) => {
            const revenue = Number(app.revenue);
            if (!Number.isFinite(revenue) || revenue <= 0) {
                return sum;
            }
            return sum + revenue;
        }, 0);
    };

    const getResponseRate = () => {
        const totalApplications = applications.length;
        const respondedApplications = applications.filter(app => 
            app.status === 'responded' || app.status === 'closed_won' || app.status === 'closed_lost'
        ).length;
        return totalApplications > 0 ? Math.round((respondedApplications / totalApplications) * 100) : 0;
    };

    const getPendingApplications = () => {
        return applications.filter(app => app.status === 'pending').length;
    };

    const getClosedWonApplications = () => {
        return applications.filter(app => app.status === 'closed_won').length;
    };

    const getClosedLostApplications = () => {
        return applications.filter(app => app.status === 'closed_lost').length;
    };

    const daysSince = (date: string | undefined) => {
        if (!date) return Infinity;
        return Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
    };

    const needsFollowUp = (application: SponsorApplication) => {
        if (application.status !== 'pending') return false;
        // 3+ days since the last touch (original pitch or most recent follow-up)
        const lastTouch = application.followUpDate || application.dateApplied;
        return daysSince(lastTouch) >= 3;
    };

    const isClosedStatus = (application: SponsorApplication) =>
        application.status === 'closed_won' || application.status === 'closed_lost';

    // Active tab, sorted so what actually needs a decision (overdue
    // follow-ups) surfaces first instead of wherever it happens to fall by
    // date — the whole point of defaulting here is to show what to act on
    // next, not a full history.
    const activeApplications = [...applications]
        .filter((app) => !isClosedStatus(app))
        .sort((a, b) => {
            const aPriority = needsFollowUp(a) ? 0 : 1;
            const bPriority = needsFollowUp(b) ? 0 : 1;
            if (aPriority !== bPriority) return aPriority - bPriority;
            return new Date(b.dateApplied).getTime() - new Date(a.dateApplied).getTime();
        });

    const closedApplications = [...applications]
        .filter(isClosedStatus)
        .sort((a, b) => new Date(b.dateApplied).getTime() - new Date(a.dateApplied).getTime());

    const visibleApplications =
        applicationsView === 'active' ? activeApplications
            : applicationsView === 'closed' ? closedApplications
                : applications;

    const getROI = () => {
        const totalRevenue = getTotalRevenue();
        const subscriptionCost = 29; // Monthly subscription cost
        return totalRevenue > 0 ? Math.round((totalRevenue / subscriptionCost) * 100) : 0;
    };

    const getRevenueThisMonth = () => {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        
        return applications
            .filter(app => {
                if (!app.revenue) return false;
                const appDate = new Date(app.dateApplied);
                return appDate.getMonth() === currentMonth && appDate.getFullYear() === currentYear;
            })
            .reduce((sum, app) => sum + (app.revenue || 0), 0);
    };

    const getRevenueLastMonth = () => {
        const currentDate = new Date();
        const lastMonth = currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
        const lastMonthYear = currentDate.getMonth() === 0 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
        
        return applications
            .filter(app => {
                if (!app.revenue) return false;
                const appDate = new Date(app.dateApplied);
                return appDate.getMonth() === lastMonth && appDate.getFullYear() === lastMonthYear;
            })
            .reduce((sum, app) => sum + (app.revenue || 0), 0);
    };

    const getMonthlyRevenueTrend = () => {
        const now = new Date();
        const months: { label: string; total: number }[] = [];
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const total = applications
                .filter(app => {
                    if (!app.revenue) return false;
                    const appDate = new Date(app.dateApplied);
                    return appDate.getMonth() === d.getMonth() && appDate.getFullYear() === d.getFullYear();
                })
                .reduce((sum, app) => sum + (app.revenue || 0), 0);
            months.push({ label: d.toLocaleDateString('en-US', { month: 'short' }), total });
        }
        return months;
    };

    const getRevenueGrowth = () => {
        const thisMonth = getRevenueThisMonth();
        const lastMonth = getRevenueLastMonth();
        if (lastMonth === 0) return thisMonth > 0 ? 100 : 0;
        return Math.round(((thisMonth - lastMonth) / lastMonth) * 100);
    };

    const handleMarkResponse = async (applicationId: string) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            await axios.put(`${config.backendUrl}userApplications/${applicationId}`, {
                status: 'responded',
                responseDate: new Date().toISOString()
            }, {
                headers: { 'x-auth-token': token }
            });
            
            setApplications(prev => prev.map(app => 
                app._id === applicationId 
                    ? { ...app, status: 'responded' as const, responseDate: new Date().toISOString() }
                    : app
            ));
        } catch (err) {
            console.error('Error updating application status:', err);
        }
    };

    const handleMarkClosedWon = (application: SponsorApplication) => {
        setSelectedApplication(application);
        setRevenueAmount('');
        setShowRevenueModal(true);
    };

    const handleConfirmRevenue = async () => {
        if (selectedApplication && revenueAmount) {
            const revenue = parseInt(revenueAmount);
            if (!isNaN(revenue) && revenue > 0) {
                try {
                    const token = localStorage.getItem('token');
                    if (!token) return;
                    
                    await axios.put(`${config.backendUrl}userApplications/${selectedApplication._id}`, {
                        status: 'closed_won',
                        revenue
                    }, {
                        headers: { 'x-auth-token': token }
                    });
                    
                    setApplications(prev => prev.map(app => 
                        app._id === selectedApplication._id 
                            ? { ...app, status: 'closed_won' as const, revenue }
                            : app
                    ));
                    setShowRevenueModal(false);
                    setSelectedApplication(null);
                    setRevenueAmount('');
                } catch (err) {
                    console.error('Error updating application revenue:', err);
                }
            }
        }
    };

    const handleMarkClosedLost = async (applicationId: string) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            await axios.put(`${config.backendUrl}userApplications/${applicationId}`, {
                status: 'closed_lost'
            }, {
                headers: { 'x-auth-token': token }
            });
            
            setApplications(prev => prev.map(app => 
                app._id === applicationId 
                    ? { ...app, status: 'closed_lost' as const }
                    : app
            ));
        } catch (err) {
            console.error('Error updating application status:', err);
        }
    };

    const handleFollowUp = (item: SponsorApplication) => {
        // Same grounded-in-real-data approach as PaidSponsorTable's
        // generateEmailTemplate — no fabricated "growing community" filler
        // when there's no real audience number, and signs with the actual
        // account name (not just the newsletter name) when we have one.
        const info = user?.newsletterInfo;
        const newsletterName = info?.name || 'my newsletter';
        const newsletterTopic = info?.topic || '';
        const audienceSize = info?.audience_size || 0;
        const engagementRate = info?.engagement_rate || 0;
        const publishingFreq = info?.publishing_frequency || '';
        const demographics = info?.audience_demographics;
        const senderName = user?.name?.trim();

        const statLines: string[] = [];
        if (audienceSize > 0) statLines.push(`${audienceSize.toLocaleString()} subscribers`);
        if (engagementRate > 0) statLines.push(`${engagementRate}% engagement rate`);
        if (publishingFreq) statLines.push(`published ${publishingFreq}`);
        if (demographics) {
            const parts = [];
            if (demographics.age_range) parts.push(`ages ${demographics.age_range}`);
            if (demographics.income_range) parts.push(`${demographics.income_range} income`);
            if (demographics.location) parts.push(demographics.location);
            if (parts.length > 0) statLines.push(`audience mostly ${parts.join(', ')}`);
        }
        const stats = statLines.length > 0 ? statLines.map(s => `- ${s}`).join('\n') : '';
        const signOff = senderName ? `${senderName}\n${newsletterName}` : newsletterName;

        const subject = `Following up — ${newsletterName}`;
        const body = [
            'Hi there,',
            `Wanted to follow up on my note about a sponsorship for ${newsletterName}${newsletterTopic ? ` (${newsletterTopic})` : ''}.`,
            stats,
            'Still open to discussing it? Happy to share more details.',
            `Best,\n${signOff}`
        ].filter(Boolean).join('\n\n');

        const mailtoLink = `mailto:${item.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;

        // Record the follow-up so the button goes quiet for another 3 days
        const followUpDate = new Date().toISOString();
        setApplications(prev => prev.map(a => a._id === item._id ? { ...a, followUpDate } : a));
        const token = localStorage.getItem('token');
        if (token) {
            axios.put(`${config.backendUrl}userApplications/${item._id}`, { followUpDate }, {
                headers: { 'x-auth-token': token }
            }).catch(err => console.error('Failed to record follow-up date', err));
        }
    };

    if (loading) {
        return (
            <div className="page-shell page-shell--wide analytics-page">
                <div className="analytics-loading">
                    <FontAwesomeIcon icon={faChartLine} spin />
                    <p>Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-shell page-shell--wide analytics-page">
                <div className="analytics-error">
                    <p>{error}</p>
                    <button type="button" className="btn btn-primary btn-sm" onClick={fetchAnalyticsData}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const totalRevenue = getTotalRevenue();
    const showRoiCard = totalRevenue > 0;
    const lastMonthRevenue = getRevenueLastMonth();
    const showRevenueGrowth = lastMonthRevenue > 0;
    const revenueGrowth = getRevenueGrowth();
    const hasApplications = applications.length > 0;

    return (
        <div className="page-shell page-shell--wide analytics-page">
            <header className="page-header">
                <h1 className="page-title">Analytics</h1>
                <p className="page-lead">
                    Track your outreach and revenue performance
                </p>
            </header>

            <div className="analytics-content">
                <div className={`analytics-overview${showRoiCard ? ' analytics-overview--four' : ''}`}>
                    <div className="overview-card revenue-card">
                        <div className="card-icon">
                            <FontAwesomeIcon icon={faDollarSign} />
                        </div>
                        <div className="card-content">
                            <div className="card-title">This Month's Revenue</div>
                            <div className="card-value">${getRevenueThisMonth().toLocaleString()}</div>
                            {showRevenueGrowth && (
                                <div className={`card-change ${revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                                    {revenueGrowth >= 0 ? '+' : ''}{revenueGrowth}% vs last month
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="overview-card response-card">
                        <div className="card-icon">
                            <FontAwesomeIcon icon={faReply} />
                        </div>
                        <div className="card-content">
                            <div className="card-title">Outreach Response Rate</div>
                            <div className="card-value">{getResponseRate()}%</div>
                            <div className="card-benchmark">vs 8% industry avg</div>
                        </div>
                    </div>

                    <div className="overview-card conversations-card">
                        <div className="card-icon">
                            <FontAwesomeIcon icon={faDatabase} />
                        </div>
                        <div className="card-content">
                            <div className="card-title">Application Status</div>
                            <div className="card-value">{applications.length}</div>
                            <div className="card-subtitle">
                                {getPendingApplications()} pending • {getClosedWonApplications()} won • {getClosedLostApplications()} lost
                            </div>
                        </div>
                    </div>

                    {showRoiCard && (
                    <div className="overview-card roi-card">
                        <div className="card-icon">
                            <FontAwesomeIcon icon={faArrowTrendUp} />
                        </div>
                        <div className="card-content">
                            <div className="card-title">ROI on SponsorDB</div>
                            <div className="card-value">{getROI()}%</div>
                            <div className="card-calculation">Revenue generated / subscription cost</div>
                        </div>
                    </div>
                    )}
                </div>

                <div className="analytics-section">
                    <div className="section-header">
                        <h3>Outreach Pipeline</h3>
                    </div>
                    {pipelineSponsors.length === 0 ? (
                        <div className="analytics-empty">
                            <p className="analytics-empty__text">
                                Nothing tracked yet - pitch a sponsor from the database and it will appear here.
                            </p>
                        </div>
                    ) : (
                        <div className="pipeline-board">
                            {[
                                { key: 'contacted', label: 'Contacted' },
                                { key: 'responded', label: 'Replied' },
                                { key: 'deal_closed', label: 'Won' },
                            ].map(col => {
                                const items = pipelineSponsors.filter(sp =>
                                    (sp.outreachStatus || (sp.isApplied ? 'contacted' : null)) === col.key
                                );
                                return (
                                    <div key={col.key} className="pipeline-column">
                                        <div className="pipeline-column__header">
                                            {col.label} <span className="pipeline-column__count">{items.length}</span>
                                        </div>
                                        {items.map(sp => (
                                            <div key={sp._id} className="pipeline-card">
                                                <button
                                                    type="button"
                                                    className="pipeline-card__remove"
                                                    onClick={() => updateOutreachStatus(sp._id, 'none')}
                                                    aria-label={`Remove ${sp.sponsorName} from your pipeline`}
                                                    title="Remove from pipeline"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                                <a
                                                    href={`https://${sp.rootDomain}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pipeline-card__name"
                                                >
                                                    {sp.sponsorName}
                                                </a>
                                                {sp.outreachDate && (
                                                    <span className="pipeline-card__date">
                                                        {new Date(sp.outreachDate).toLocaleDateString()}
                                                    </span>
                                                )}
                                                <select
                                                    className="pipeline-card__select"
                                                    value={sp.outreachStatus || 'contacted'}
                                                    onChange={(e) => updateOutreachStatus(sp._id, e.target.value)}
                                                >
                                                    <option value="contacted">Contacted</option>
                                                    <option value="responded">Replied</option>
                                                    <option value="deal_closed">Won</option>
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="analytics-section">
                    <div className="section-header">
                        <h3>Revenue Tracking</h3>
                    </div>

                    {!hasApplications ? (
                        <div className="analytics-empty">
                            <FontAwesomeIcon icon={faEnvelope} className="analytics-empty__icon" />
                            <p className="analytics-empty__text">
                                No outreach tracked yet. Start by contacting a sponsor.
                            </p>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate('/sponsors')}
                            >
                                Browse sponsors
                            </button>
                        </div>
                    ) : (
                    <>
                    <div className="applications-tabs">
                        {([
                            { key: 'active', label: 'Needs Attention', count: activeApplications.length },
                            { key: 'closed', label: 'Closed', count: closedApplications.length },
                            { key: 'all', label: 'All', count: applications.length },
                        ] as const).map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                className={`applications-tab ${applicationsView === tab.key ? 'active' : ''}`}
                                onClick={() => setApplicationsView(tab.key)}
                            >
                                {tab.label}
                                <span className="applications-tab__count">{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    {visibleApplications.length === 0 ? (
                        <div className="analytics-empty">
                            <p className="analytics-empty__text">
                                {applicationsView === 'active'
                                    ? "Nothing needs a decision right now — you're all caught up."
                                    : 'No closed deals yet.'}
                            </p>
                        </div>
                    ) : (
                    <div className="applications-table">
                        <div className="table-header">
                            <div className="table-col">Sponsor</div>
                            <div className="table-col">Contact</div>
                            <div className="table-col">Applied Date</div>
                            <div className="table-col">Status</div>
                            <div className="table-col">Revenue</div>
                            <div className="table-col">Actions</div>
                        </div>
                        {visibleApplications.map((app) => (
                            <div key={app._id} className="table-row">
                                <div className="table-col table-col--sponsor">
                                    <div className="sponsor-name">{app.sponsorName}</div>
                                </div>
                                <div className="table-col table-col--contact">
                                    <div className="sponsor-email" title={app.contactEmail}>{app.contactEmail}</div>
                                </div>
                                <div className="table-col table-col--date">
                                    {new Date(app.dateApplied).toLocaleDateString()}
                                </div>
                                <div className="table-col table-col--status">
                                    <span className={`status-badge ${app.status}`}>
                                        {app.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                </div>
                                <div className={`table-col table-col--revenue ${!app.revenue ? 'table-col--empty' : ''}`}>
                                    {app.revenue ? `$${app.revenue.toLocaleString()}` : '-'}
                                </div>
                                <div className="table-col actions-col">
                                    {app.status === 'pending' && (
                                        <div className="action-buttons">
                                            <button
                                                type="button"
                                                className="action-btn response-btn"
                                                onClick={() => handleMarkResponse(app._id)}
                                            >
                                                <FontAwesomeIcon icon={faReply} />
                                                Mark Responded
                                            </button>
                                            {needsFollowUp(app) ? (
                                                <button
                                                    type="button"
                                                    className="action-btn follow-up-btn"
                                                    onClick={() => handleFollowUp(app)}
                                                    title="Send a follow-up email (opens your mail client)"
                                                >
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                    Follow up
                                                </button>
                                            ) : app.followUpDate ? (
                                                <span className="follow-up-hint">
                                                    Followed up {daysSince(app.followUpDate) === 0 ? 'today' : `${daysSince(app.followUpDate)}d ago`}
                                                </span>
                                            ) : null}
                                        </div>
                                    )}
                                    {app.status === 'responded' && (
                                        <div className="action-buttons">
                                            <button 
                                                className="action-btn success-btn"
                                                onClick={() => handleMarkClosedWon(app)}
                                            >
                                                <FontAwesomeIcon icon={faCheck} />
                                                Won
                                            </button>
                                            <button 
                                                className="action-btn danger-btn"
                                                onClick={() => handleMarkClosedLost(app._id)}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                                Lost
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    )}
                    </>
                    )}
                </div>

                <div className="analytics-section">
                    <div className="section-header">
                        <h3>Monthly Revenue Trend</h3>
                    </div>
                    {totalRevenue === 0 ? (
                        <div className="analytics-empty">
                            <FontAwesomeIcon icon={faChartLine} className="analytics-empty__icon" />
                            <p className="analytics-empty__text">
                                Record revenue on a won deal and your trend shows up here.
                            </p>
                        </div>
                    ) : (
                        <div className="revenue-trend">
                            {(() => {
                                const months = getMonthlyRevenueTrend();
                                const max = Math.max(...months.map(m => m.total), 1);
                                return months.map((m, i) => (
                                    <div key={i} className="revenue-trend__col">
                                        <div className="revenue-trend__bar-track">
                                            <div
                                                className="revenue-trend__bar"
                                                style={{ height: `${m.total > 0 ? Math.max((m.total / max) * 100, 6) : 0}%` }}
                                                title={`$${m.total.toLocaleString()}`}
                                            />
                                        </div>
                                        <div className="revenue-trend__value">{m.total > 0 ? `$${m.total.toLocaleString()}` : ''}</div>
                                        <div className="revenue-trend__label">{m.label}</div>
                                    </div>
                                ));
                            })()}
                        </div>
                    )}
                </div>
            </div>

            {/* Revenue Modal */}
            {showRevenueModal && selectedApplication && (
                <div className="modal-overlay">
                    <div className="revenue-modal">
                        <div className="modal-header">
                            <h3>Record Revenue</h3>
                            <button 
                                className="modal-close"
                                onClick={() => setShowRevenueModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-content">
                            <p>How much revenue did you generate from <strong>{selectedApplication.sponsorName}</strong>?</p>
                            <div className="revenue-input-group">
                                <span className="currency-symbol">$</span>
                                <input
                                    type="number"
                                    value={revenueAmount}
                                    onChange={(e) => setRevenueAmount(e.target.value)}
                                    placeholder="0"
                                    className="revenue-input"
                                    autoFocus
                                />
                            </div>
                            <div className="modal-actions">
                                <button 
                                    className="btn-cancel"
                                    onClick={() => setShowRevenueModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn-confirm"
                                    onClick={handleConfirmRevenue}
                                    disabled={!revenueAmount || parseInt(revenueAmount) <= 0}
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                    Record Revenue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Analytics; 